/**
 * Reads the website's single content sheet.
 *
 * The sheet is a row of "blocks" side by side. Row 1 holds each block's name in square
 * brackets ([HERO], [WATCH], ...), row 2 is a hint for each column (not read), row 3 holds
 * the column headings, and the data starts in row 4. Two kinds of block:
 *   - settings blocks: columns "Setting | Value | Notes", one setting per row
 *   - list blocks: one item per row (add rows freely; a "Show" column hides a row when "no")
 * Columns are found by their heading text, so blocks can be moved and columns re-ordered.
 */
import { clean, ytId } from './parse';
import { SECTIONS, SITE } from '../config';

const key = (s) => clean(s).toLowerCase().replace(/[^a-z0-9]+/g, '');
const isYes = (v) => !/^(no|n|false|off|hide|hidden|0)$/i.test(clean(v));
const yes = (v, dflt = true) => (v === undefined || v === '' ? dflt : isYes(v));
const paras = (text) => (text || '').split(/\n+/).map((p) => p.trim()).filter(Boolean);
const percent = (v, dflt) => { const s = clean(v); if (!s) return dflt; return /%$/.test(s) ? s : `${parseFloat(s)}%`; };

export function readBlocks(rows) {
  const blocks = {};
  const top = rows[0] || [];
  const headings = rows[2] || [];
  top.forEach((cell, col) => {
    const m = /^\s*\[(.+?)\]\s*$/.exec(clean(cell));
    if (!m) return;
    const cols = [];
    for (let c = col; clean(headings[c]); c += 1) cols.push({ col: c, name: key(headings[c]) });
    const data = [];
    rows.slice(3).forEach((row, i) => {
      const item = { _row: i + 4 };
      let any = false;
      cols.forEach((h) => { const v = clean(row[h.col]); item[h.name] = v; if (v) any = true; });
      if (any) data.push(item);
    });
    blocks[key(m[1])] = cols[0] && cols[0].name === 'setting'
      ? { kv: Object.fromEntries(data.filter((d) => d.setting).map((d) => [key(d.setting), d.value])) }
      : { rows: data };
  });
  return blocks;
}

const ART_NAMES = { capitalism: 'capitalism', wall: 'capitalism', education: 'education', environmentalism: 'environmentalism', nature: 'environmentalism', immigration: 'immigration' };

export function buildContent(rows) {
  const b = readBlocks(rows || []);
  const kv = (name) => (b[name] && b[name].kv) || {};
  const list = (name) => ((b[name] && b[name].rows) || []).filter((r) => isYes(r.show));

  // ---- site-wide ---------------------------------------------------------------
  const s = kv('site');
  const site = {
    name: s.sitename || SITE.name,
    email: s.contactemail || SITE.email,
    instagram: s.instagramlink || SITE.instagram,
    instagramHandle: s.instagramhandle || '@ize_trio',
    youtube: s.youtubelink || SITE.youtube,
    pressLabel: s.presskitlabel || 'Press kit',
    pressLink: s.presskitlink || '/EPK',
    showPress: yes(s.showpresskitlink),
    copyright: s.copyrighttext || `${s.sitename || SITE.name}. All rights reserved.`,
  };

  // ---- sections: menu label, heading, visibility --------------------------------
  const defaults = {
    about: { label: 'About', heading: 'About' }, music: { label: 'Music', heading: 'Music' },
    watch: { label: 'Watch', heading: 'Watch' }, live: { label: 'Live', heading: 'Live' },
    band: { label: 'Band', heading: 'The band' }, praise: { label: '', heading: 'Praise' },
    support: { label: 'Support', heading: 'Support' }, contact: { label: 'Contact', heading: 'Contact' },
  };
  const sectionRows = {};
  ((b.sections && b.sections.rows) || []).forEach((r) => { sectionRows[key(r.section)] = r; });
  const sec = {};
  Object.keys(defaults).forEach((id) => {
    const r = sectionRows[id];
    sec[id] = r
      ? { label: r.menulabel, heading: r.heading || defaults[id].heading, show: isYes(r.show) }
      : { ...defaults[id], show: true };
  });
  const navOrder = SECTIONS.map((x) => x.id);

  // ---- hero --------------------------------------------------------------------
  const h = kv('hero');
  const rel = kv('featuredrelease');
  const release = {
    show: yes(rel.showfeaturedrelease) && Boolean(rel.albumtitle),
    title: rel.albumtitle || '',
    badge: rel.badgetext || '',
    cover: rel.coverimage || '',
    coverLink: rel.coverlink || '',
    quote: rel.quote || '',
    quoteSource: rel.quotesource || '',
    links: list('featuredlinks').filter((l) => l.link).map((l) => ({ key: l._row, label: l.buttonlabel, url: l.link })),
  };
  let pill = null;
  if (yes(h.showpill)) {
    if (h.pilltitle) pill = { title: h.pilltitle, text: h.pilltext, image: h.pillimage, link: h.pilllink || '#music' };
    else if (release.show) pill = { title: release.title, text: release.badge || 'New release', image: release.cover, link: h.pilllink || '#music' };
  }
  // Buttons: one row per button in the [HERO BUTTONS] block (as many as you like). Sheets from before
  // that block existed still work: "Button 1 label", "Button 1 link", "Button 1 show" ... in [HERO].
  let buttonRows = list('herobuttons').filter((r) => r.label)
    .map((r) => ({ label: r.label, url: r.link, style: r.style }));
  if (!b.herobuttons) {
    const nums = [...new Set(Object.keys(h).map((k) => /^button(\d+)(?:label|link|show)$/.exec(k)).filter(Boolean).map((m) => Number(m[1])))].sort((x, y) => x - y);
    buttonRows = nums.filter((n) => h[`button${n}label`] && yes(h[`button${n}show`]))
      .map((n) => ({ label: h[`button${n}label`], url: h[`button${n}link`], style: '' }));
  }
  const heroButtons = buttonRows.map((r, i) => ({
    label: r.label,
    url: r.url || '#music',
    style: /solid|fill|white/i.test(r.style) ? 'btn--light' : /outline|ghost|line/i.test(r.style) ? 'btn--ghost-light' : (i === 0 ? 'btn--light' : 'btn--ghost-light'),
  }));

  const hero = {
    line1: h.titleline1 || 'Ize',
    line2: h.titleline2 || 'Trio',
    tagline: h.tagline || '',
    photo: h.photo || '',
    focus: h.photofocus || '50% 35%',
    alt: h.photodescription || 'The members of Ize Trio',
    buttons: heroButtons,
    pill,
  };

  const quote = (r) => ({ key: r._row, quote: r.quote, source: r.source });

  // ---- about -------------------------------------------------------------------
  const a = kv('about');
  const about = {
    heading: sec.about.heading,
    intro: paras(a.introparagraph),
    photo: a.photo || '',
    focus: a.photofocus || '50% 14%',
    alt: a.photodescription || 'Ize Trio',
    story: list('aboutstory').filter((r) => r.paragraph).map((r) => r.paragraph),
    links: list('aboutlinks').filter((l) => l.link && l.buttonlabel).map((l) => ({ key: l._row, label: l.buttonlabel, url: l.link })),
  };

  // ---- music / watch / live ----------------------------------------------------
  const labels = kv('labels');
  const recordings = list('morerecordings').filter((r) => r.title).map((r) => ({
    key: r._row, title: r.title, kind: r.type || 'Album', image: r.coverimage, href: r.link, desc: r.description,
  }));
  const videos = list('watch').map((r) => ({ key: r._row, title: r.title || 'Video', id: ytId(r.youtubelinkorid || ''), desc: r.description }))
    .filter((v) => v.id);
  const l = kv('live');
  const live = {
    heading: sec.live.heading,
    emptyTitle: l.emptymessage || 'No shows are on the calendar at the moment.',
    emptyText: l.emptytext || '',
    emptyButton: l.emptybuttonlabel || 'Get concert updates',
    detailsButton: l.detailsbuttonlabel || 'Details and tickets',
    keyword: l.onlyshoweventscontaining === undefined ? 'Ize' : l.onlyshoweventscontaining,
  };

  // ---- band / praise -----------------------------------------------------------
  const band = list('band').filter((r) => r.name).map((r) => ({
    key: r._row, name: r.name, role: r.role, photo: r.photo, focus: r.photofocus || '50% 30%',
    bio: paras(r.bio), site: r.website,
  }));

  // ---- support / contact -------------------------------------------------------
  const sp = kv('support');
  const support = {
    heading: sec.support.heading,
    subheading: sp.subheading || '',
    mission: paras(sp.missiontext),
    extra: paras(sp.extratext),
    thanks: sp.thankyouline || '',
    ways: list('supportways').filter((r) => r.title).map((r) => ({
      key: r._row, title: r.title, image: r.image, desc: paras(r.description), button: r.buttonlabel || 'Donate', url: r.link,
    })),
  };
  const c = kv('contact');
  const contact = {
    heading: sec.contact.heading,
    intro: c.introtext || '',
    igHeading: c.instagramheading || '',
    igText: c.instagramtext || '',
    igPhoto: c.instagramphoto || '',
    newsHeading: c.newsletterheading || '',
    newsSrc: c.newsletterformlink || '',
    newsHeight: parseInt(c.newsletterformheight, 10) || 380,
  };

  // ---- artwork placement -------------------------------------------------------
  const art = {};
  list('art').forEach((r) => {
    const name = ART_NAMES[key(r.artwork)];
    const id = key(r.section);
    if (!name || !id) return;
    const style = /sky|horizon/i.test(r.style) ? 'skyline' : 'mandala';
    (art[id] = art[id] || []).push({
      key: r._row, style, name,
      side: /left/i.test(r.side) ? 'left' : 'right',
      top: percent(r.height, '6%'),
      turn: /anti|counter/i.test(r.direction) ? -1 : 1,
    });
  });

  const autoLinks = {};
  list('autolinks').forEach((r) => { if (r.text && r.link) autoLinks[r.text] = r.link; });

  return {
    ready: Boolean(rows && rows.length),
    site, sec, navOrder, hero,
    heroQuotes: list('heroquotes').filter((r) => r.quote).map(quote),
    about, release, recordings, videos, live, band,
    recognition: list('recognition').filter((r) => r.text).map((r) => ({ key: r._row, text: r.text })),
    labels: {
      recognition: labels.recognitionheading || 'Recognition',
      recordings: labels.morerecordingsheading || 'More recordings',
      readMore: labels.readmorelabel || 'Read more',
      showLess: labels.showlesslabel || 'Show less',
    },
    praise: list('praise').filter((r) => r.quote).map(quote),
    support, contact, art, autoLinks,
  };
}
